import React, { useEffect, useState } from "react";
import clsx from "clsx";
import Modal from "../Shared/Modal";
import EmailDeliveryOptions, { emailSettingsFor } from "./EmailDeliveryOptions";

const formatLastSent = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
};

/**
 * The "Send to email" screen of one scheduled task: the owner's switch and the list of other
 * recipients, saved as one settings object.
 *
 * `onSave(settings)` is expected to call the API and may throw; the message of the failure is
 * shown in the dialog so the user sees why (a bad address, say) instead of a silent close.
 */
const ScheduledEmailSettingsModal = ({
  open,
  title,
  ownerEmail,
  settings,
  max,
  onSave,
  onClose,
}) => {
  const [sendToMe, setSendToMe] = useState(false);
  const [extraRecipients, setExtraRecipients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSendToMe(!!settings?.sendToMe && !!ownerEmail);
    setExtraRecipients(settings?.extraRecipients ?? []);
    setError(null);
    setSaving(false);
  }, [open, settings, ownerEmail]);

  if (!open) return null;

  const lastSent = formatLastSent(settings?.lastEmailedAtUtc);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(emailSettingsFor(sendToMe, ownerEmail, extraRecipients));
      onClose();
    } catch (e) {
      const message =
        (typeof e?.response?.data === "string" && e.response.data) ||
        e?.response?.data?.message ||
        "The email settings could not be saved. Please try again.";
      setError(message);
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="w-full md:min-w-[640px] md:max-w-[700px]">
        <div className="flex items-center justify-between border-b border-whitesmoke-100 px-6 py-4 text-xl font-bold text-dark-blue-100">
          Send to email
          <button type="button" aria-label="Close" className="hover:cursor-pointer" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4L16 16M16 4L4 16" stroke="#303C54" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6">
          <div className="rounded-base bg-whitesmoke-600 px-4 py-3">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-dimgray-200">Scheduled prompt</p>
            <p className="mt-1 text-[14px] font-semibold text-dark-blue-100 [overflow-wrap:anywhere]">“{title}”</p>
            <p className="mt-1 text-[12px] text-dimgray-200">
              Every time this prompt runs, the answer is emailed as a nicely formatted message with a PDF copy attached.
              {lastSent && ` Last emailed ${lastSent}.`}
              {settings?.ownerUnsubscribed &&
                " You unsubscribed from this report by email; switch “Send to my email” back on to receive it again."}
            </p>
          </div>

          <EmailDeliveryOptions
            ownerEmail={ownerEmail}
            sendToMe={sendToMe}
            onSendToMeChange={setSendToMe}
            extraRecipients={extraRecipients}
            onExtraRecipientsChange={setExtraRecipients}
            max={max}
          />

          {error && (
            <p role="alert" className="rounded-base bg-red-100/10 px-4 py-2.5 text-[13px] text-red-100">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-2xl border border-dimgray-200 px-6 py-2.5 text-sm font-semibold text-dark-blue-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={clsx(
                "rounded-2xl border border-dimgray-200 bg-dark-blue-100 px-6 py-2.5 text-sm font-bold text-white",
                saving && "opacity-70"
              )}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduledEmailSettingsModal;
